import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

// Components
import { PageHeader } from "../components/PageSpecificComponents";

// Containers
import ChangePasswordForm from "../containers/forms/accountSettings/changePasswordForm/ChangePassword.form";

// Icons
import {
  Building,
  Camera,
  Check,
  Devices,
  DropDownArrow,
  Info,
  Lock,
  LogoutIcon,
  Mail,
  PhoneIcon,
  Shield,
  Calendar
} from "../SVGS";

// Actions
import { logout, setActiveBranch } from "../redux/actions/app.actions";
import {
  fetchEnabledNotifications,
  updateEnabledNotifications
} from "../redux/actions/notifications.actions";

// Constants
import * as tabsValues from "../constants/accountSettingsTabs.constants";

// Utils
import { isEmpty } from "../utils";

// Styles
import { AccountSettingsStyles } from "../pagesStyles/accountSettings.styles";

const PAGE_TABS = [
  { id: tabsValues.PROFILE, label: "Personal info" },
  { id: tabsValues.ROLE, label: "Role & access" },
  { id: tabsValues.SECURITY, label: "Security" },
  { id: tabsValues.NOTIFICATIONS, label: "Notifications" },
  { id: tabsValues.PREFERENCES, label: "Preferences" }
];

const DEFAULT_PERMISSIONS = [
  "Manage branch settings",
  "Manage staff & schedules",
  "Approve service requests",
  "View analytics",
  "Edit business profile",
  "Manage service menu"
];

const Avatar = ({ user }) => {
  const initials = `${user?.firstName?.[0] || ""}${
    user?.lastName?.[0] || ""
  }`.toUpperCase();
  const imageUrl = user?.profileImage?.url;

  return (
    <div className="avatarWrap">
      {imageUrl ? (
        <img src={imageUrl} alt={`${user?.firstName} ${user?.lastName}`} />
      ) : (
        <div className="avatarInit">{initials || "U"}</div>
      )}
      <button
        type="button"
        className="avatarEdit"
        aria-label="Change photo"
        title="Change photo"
      >
        <Camera stroke="#FFFFFF" />
      </button>
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
  <div className="secHead">
    <div className="secHeadLeft">
      {Icon && (
        <span className="secHeadIcon">
          <Icon />
        </span>
      )}
      <div>
        <div className="secHeadTitle">{title}</div>
        {subtitle && <div className="secHeadSub">{subtitle}</div>}
      </div>
    </div>
    {action && <div className="secHeadAction">{action}</div>}
  </div>
);

const Field = ({ label, hint, labelAccessory, children }) => (
  <label className="field">
    <span className="fieldLabelRow">
      <span className="fieldLabel">{label}</span>
      {labelAccessory && (
        <span className="fieldLabelAccessory">{labelAccessory}</span>
      )}
    </span>
    <span className="fieldControl">{children}</span>
    {hint && <span className="fieldHint">{hint}</span>}
  </label>
);

const Select = ({ value, options, onChange, name }) => (
  <span className="selWrap">
    <select
      className="input select"
      value={value}
      name={name}
      onChange={e => onChange?.(e.target.value)}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <span className="selCaret">
      <DropDownArrow />
    </span>
  </span>
);

const Toggle = ({ value, onChange, label, sub }) => (
  <button
    type="button"
    className={`toggleRow ${value ? "isOn" : ""}`}
    onClick={() => onChange?.(!value)}
  >
    <span className="toggleText">
      <span className="toggleLabel">{label}</span>
      {sub && <span className="toggleSub">{sub}</span>}
    </span>
    <span className="toggleSwitch" aria-checked={value} role="switch">
      <span className="toggleThumb" />
    </span>
  </button>
);

const ProfileTab = ({ user }) => {
  const [form, setForm] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: user?.contactNumber || ""
  });

  const update = key => e =>
    setForm(prev => ({ ...prev, [key]: e.target.value }));

  return (
    <div className="grid">
      <aside className="aside">
        <div className="asideTitle">Personal information</div>
        <div className="asideSub">
          Your name, contact info and profile photo. This is visible to managers
          and staff at your branch.
        </div>
      </aside>
      <div className="cards">
        <div className="cardSurface">
          <div className="gridTwo">
            <Field label="First name">
              <input
                className="input"
                value={form.firstName}
                onChange={update("firstName")}
                name="firstName"
              />
            </Field>
            <Field label="Last name">
              <input
                className="input"
                value={form.lastName}
                onChange={update("lastName")}
                name="lastName"
              />
            </Field>
            <Field
              label="Email address"
              labelAccessory={
                <>
                  <Check /> Verified
                </>
              }
            >
              <input
                className="input"
                value={form.email}
                onChange={update("email")}
                name="email"
              />
            </Field>
            <Field
              label="Phone number"
              hint="Used for SMS alerts on urgent service requests."
            >
              <input
                className="input"
                value={form.phone}
                onChange={update("phone")}
                name="phone"
              />
            </Field>
          </div>
          <div className="rowEnd">
            <button type="button" className="btn btnGhost">
              Discard
            </button>
            <button type="button" className="btn btnPrimary">
              Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RoleTab = ({ user, role, branches, activeBranch, onSwitchBranch }) => {
  return (
    <div className="grid">
      <aside className="aside">
        <div className="asideTitle">Role &amp; access</div>
        <div className="asideSub">
          What you can do in TruckFix is determined by your role. Roles are
          assigned by your branch owner.
        </div>
      </aside>
      <div className="cards">
        <div className="cardSurface">
          <div className="roleHero">
            <div className="roleIcon">
              <Shield stroke="#FFFFFF" />
            </div>
            <div>
              <div className="roleName">{role.name}</div>
              <div className="roleDesc">{role.description}</div>
            </div>
          </div>
          <div className="permsTitle">Permissions granted</div>
          <div className="perms">
            {role.permissions.map(p => (
              <span key={p} className="permChip">
                <Check />
                {p}
              </span>
            ))}
          </div>
          <div className="callout">
            <Info />
            Need additional access? Request a role change and your branch owner
            will review it.
          </div>
          <div className="rowEnd">
            <button type="button" className="btn btnGhost">
              Request role change
            </button>
          </div>
        </div>

        <div className="cardSurface">
          <SectionHeader
            icon={Building}
            title="Assigned branches"
            subtitle="Branches where you can act under this role."
          />
          <div className="branchList">
            {branches.length === 0 && (
              <div className="branchRow">
                <div className="branchIco">
                  <Building />
                </div>
                <div className="branchMid">
                  <div className="branchName">No branches assigned</div>
                  <div className="branchAddr">
                    Ask your branch owner to assign you to a branch.
                  </div>
                </div>
              </div>
            )}
            {branches.map(b => {
              const isActive = activeBranch?.id === b.id;
              return (
                <div
                  key={b.id}
                  className={`branchRow ${isActive ? "isActive" : ""}`}
                >
                  <div className="branchIco">
                    <Building />
                  </div>
                  <div className="branchMid">
                    <div className="branchName">
                      {b.name}
                      {isActive && (
                        <span className="branchTag">Currently active</span>
                      )}
                    </div>
                    <div className="branchAddr">
                      {b.location?.address || ""}
                    </div>
                  </div>
                  {!isActive && (
                    <button
                      type="button"
                      className="btn btnGhost btnSm"
                      onClick={() => onSwitchBranch(b)}
                    >
                      Switch to this branch
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const SecurityTab = ({ sessions, onRevokeSession, onSignOutEverywhere }) => (
  <div className="grid">
    <aside className="aside">
      <div className="asideTitle">Security</div>
      <div className="asideSub">
        Manage how you sign in and review devices connected to your account.
      </div>
    </aside>
    <div className="cards">
      <div className="cardSurface">
        <SectionHeader
          icon={Lock}
          title="Password"
          subtitle="Last changed 3 months ago."
        />
        <div style={{ marginTop: 16 }}>
          <ChangePasswordForm />
        </div>
      </div>

      <div className="cardSurface">
        <SectionHeader
          icon={Shield}
          title="Two-factor authentication"
          subtitle="Add a second step when signing in using an authenticator app like 1Password or Authy."
          action={
            <button type="button" className="btn btnPrimary btnSm">
              Enable 2FA
            </button>
          }
        />
      </div>

      <div className="cardSurface">
        <SectionHeader
          icon={Devices}
          title="Active sessions"
          subtitle={`${sessions.length} devices currently signed into your account.`}
          action={
            <button
              type="button"
              className="btn btnDangerGhost btnSm"
              onClick={onSignOutEverywhere}
            >
              Sign out everywhere
            </button>
          }
        />
        <div className="sessionList">
          {sessions.map(s => (
            <div key={s.id} className="sessionRow">
              <span className="sessionIco">
                <Devices />
              </span>
              <div className="sessionMid">
                <div className="sessionDevice">
                  {s.device}
                  {s.current && <span className="sessionCurrent">Current</span>}
                </div>
                <div className="sessionMeta">
                  {s.location} · {s.lastActive}
                </div>
              </div>
              {!s.current && (
                <button
                  type="button"
                  className="linkDanger"
                  onClick={() => onRevokeSession?.(s.id)}
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const PUSH_PREF_ITEMS = [
  {
    key: "service_requests",
    label: "Service requests",
    sub: "When a driver creates a request your branch can take."
  },
  {
    key: "tickets",
    label: "Ticket assignments",
    sub: "When a service is assigned to you."
  },
  {
    key: "messages",
    label: "Chat messages",
    sub: "New messages on a service or request you're part of."
  },
  {
    key: "comments",
    label: "Service comments",
    sub: "When a teammate leaves a comment on a service."
  }
];

const NotificationsTab = () => {
  const dispatch = useDispatch();
  const { enabled, enabledSaving } = useSelector(state => state.notifications);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    dispatch(fetchEnabledNotifications());
  }, [dispatch]);

  // Sync the local draft whenever the saved preferences (re)arrive.
  useEffect(() => {
    if (enabled) setDraft(enabled);
  }, [enabled]);

  const dirty =
    draft && enabled && JSON.stringify(draft) !== JSON.stringify(enabled);

  return (
    <div className="grid">
      <aside className="aside">
        <div className="asideTitle">Notifications</div>
        <div className="asideSub">
          Choose what TruckFix pushes to your devices. Your in-app inbox is
          always on.
        </div>
      </aside>
      <div className="cards">
        <div className="cardSurface">
          <div className="callout notificationsCallout">
            <Info />
            These switches only control push notifications sent to your
            devices. Your in-app notification list always shows everything you
            have permission to see.
          </div>

          <SectionHeader
            icon={Devices}
            title="Push notifications"
            subtitle="Delivered to the web app and your installed devices."
          />

          <div className="toggleList">
            {draft &&
              PUSH_PREF_ITEMS.map(item => (
                <Toggle
                  key={item.key}
                  value={!!draft[item.key]}
                  onChange={value =>
                    setDraft(prev => ({ ...prev, [item.key]: value }))
                  }
                  label={item.label}
                  sub={item.sub}
                />
              ))}
          </div>

          <div className="rowEnd">
            <button
              type="button"
              className="btn btnGhost"
              disabled={!dirty}
              onClick={() => setDraft(enabled)}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn btnPrimary"
              disabled={!dirty || enabledSaving}
              onClick={() => dispatch(updateEnabledNotifications(draft))}
            >
              <Check /> Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreferencesTab = ({ prefs, onChange }) => (
  <div className="grid">
    <aside className="aside">
      <div className="asideTitle">Preferences</div>
      <div className="asideSub">
        Language, region and how dates and times are displayed for you.
      </div>
    </aside>
    <div className="cards">
      <div className="cardSurface">
        <div className="gridTwo">
          <Field label="Language">
            <Select
              value={prefs.language}
              onChange={v => onChange("language", v)}
              options={[
                { value: "en-US", label: "English (US)" },
                { value: "en-GB", label: "English (UK)" },
                { value: "sr-RS", label: "Srpski" },
                { value: "es-ES", label: "Español" }
              ]}
            />
          </Field>
          <Field label="Time zone">
            <Select
              value={prefs.timezone}
              onChange={v => onChange("timezone", v)}
              options={[
                { value: "ny", label: "America/New_York (EST)" },
                { value: "la", label: "America/Los_Angeles (PST)" },
                { value: "ch", label: "America/Chicago (CST)" }
              ]}
            />
          </Field>
          <Field label="Date format">
            <Select
              value={prefs.dateFormat}
              onChange={v => onChange("dateFormat", v)}
              options={[
                { value: "mdy", label: "MM/DD/YYYY" },
                { value: "dmy", label: "DD/MM/YYYY" },
                { value: "ymd", label: "YYYY-MM-DD" }
              ]}
            />
          </Field>
          <Field label="Time format">
            <Select
              value={prefs.timeFormat}
              onChange={v => onChange("timeFormat", v)}
              options={[
                { value: "12", label: "12-hour (9:00 AM)" },
                { value: "24", label: "24-hour (09:00)" }
              ]}
            />
          </Field>
        </div>
        <div className="rowEnd">
          <button type="button" className="btn btnPrimary">
            Save preferences
          </button>
        </div>
      </div>
    </div>
  </div>
);

const AccountSettings = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { user, activeBranch, branches } = useSelector(state => state.app);

  // The notification panel deep-links here with { state: { tab } }.
  const [chosenTab, setChosenTab] = useState(
    location.state?.tab || tabsValues.PROFILE
  );

  const role = useMemo(() => {
    const firstRole = user?.roles?.[0];
    if (firstRole && firstRole.name) {
      return {
        name: firstRole.name,
        description:
          firstRole.description ||
          "Manages branch operations, staff, and service requests across assigned locations.",
        permissions:
          !isEmpty(firstRole.permissions) &&
          firstRole.permissions.some(p => p.name)
            ? firstRole.permissions.map(p => p.name)
            : DEFAULT_PERMISSIONS
      };
    }
    return {
      name: "Branch Manager",
      description:
        "Manages branch operations, staff, and service requests across assigned locations.",
      permissions: DEFAULT_PERMISSIONS
    };
  }, [user]);

  const [userPrefs, setUserPrefs] = useState({
    language: "en-US",
    timezone: "ny",
    dateFormat: "mdy",
    timeFormat: "12"
  });

  const [sessions, setSessions] = useState([
    {
      id: "s1",
      device: "Chrome on macOS",
      location: "Newark, NJ · USA",
      lastActive: "Active now",
      current: true
    },
    {
      id: "s2",
      device: "iPhone 14 · Safari",
      location: "Newark, NJ · USA",
      lastActive: "2 hours ago",
      current: false
    },
    {
      id: "s3",
      device: "Chrome on Windows",
      location: "Jersey City, NJ · USA",
      lastActive: "5 days ago",
      current: false
    }
  ]);

  const setTab = useCallback(value => setChosenTab(value), []);

  const handleLogout = () => {
    dispatch(logout());
    toast.success("You have logged out successfully");
  };

  const handleSwitchBranch = branch => {
    dispatch(setActiveBranch(branch));
    toast.success(`Switched to ${branch.name}`);
  };

  const handlePrefChange = (key, value) => {
    setUserPrefs(prev => ({ ...prev, [key]: value }));
  };

  const handleRevokeSession = id => {
    setSessions(prev => prev.filter(s => s.id !== id));
    toast.success("Session revoked");
  };

  const handleSignOutEverywhere = () => {
    setSessions(prev => prev.filter(s => s.current));
    toast.success("Signed out of all other devices");
  };

  const activeBranchName = activeBranch?.name || "";

  return (
    <AccountSettingsStyles>
      <PageHeader title="Account Settings" />

      {/* Hero card */}
      <div className="hero">
        <div className="heroCover">
          <div className="heroRow">
            <Avatar user={user} />
            <div className="heroInfo">
              <div className="heroName">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="heroTagline">
                <span className="rolePillDark">
                  <Shield stroke="#FFFFFF" />
                  {role.name}
                </span>
                {activeBranchName && (
                  <>
                    <span className="heroDot" />
                    <span className="heroLineLight">
                      <Building stroke="rgba(255,255,255,0.7)" />
                      {activeBranchName}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="heroActions">
              <button
                type="button"
                className="btnOnAccentGhost"
                onClick={handleLogout}
              >
                <LogoutIcon fill="#FFFFFF" />
                Sign out
              </button>
            </div>
          </div>
        </div>
        <div className="heroFoot">
          {user?.email && (
            <span className="heroLine">
              <Mail />
              {user.email}
            </span>
          )}
          {user?.contactNumber && (
            <>
              <span className="dot" />
              <span className="heroLine">
                <PhoneIcon />
                {user.contactNumber}
              </span>
            </>
          )}
          <span className="dot" />
          <span className="heroLine">
            <Calendar fill="currentColor" />
            Member since March 2023
          </span>
        </div>
      </div>

      {/* Tab bar */}
      <nav className="tabBar">
        {PAGE_TABS.map(t => (
          <button
            key={t.id}
            type="button"
            className={`tabBtn ${chosenTab === t.id ? "isActive" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {/* Content */}
      <div className="content">
        {chosenTab === tabsValues.PROFILE && <ProfileTab user={user} />}
        {chosenTab === tabsValues.ROLE && (
          <RoleTab
            user={user}
            role={role}
            branches={branches || []}
            activeBranch={activeBranch}
            onSwitchBranch={handleSwitchBranch}
          />
        )}
        {chosenTab === tabsValues.SECURITY && (
          <SecurityTab
            sessions={sessions}
            onRevokeSession={handleRevokeSession}
            onSignOutEverywhere={handleSignOutEverywhere}
          />
        )}
        {chosenTab === tabsValues.NOTIFICATIONS && <NotificationsTab />}
        {chosenTab === tabsValues.PREFERENCES && (
          <PreferencesTab prefs={userPrefs} onChange={handlePrefChange} />
        )}
      </div>
    </AccountSettingsStyles>
  );
};

export default AccountSettings;
